<template>
  <div
    class="cbs-inline-attr"
    :class="{
      'cbs-inline-attr--focused': focused,
      'cbs-inline-attr--block': type === 'filter' && filterExpanded,
    }"
    @click.stop="$emit('click')"
  >
    <span class="cbs-inline-attr__key">{{ label }}</span>

    <!-- text editing mode -->
    <b-form-input
      v-if="editing"
      ref="input"
      v-model="editVal"
      size="sm"
      class="cbs-inline-attr__input"
      @keydown.enter.prevent="save"
      @keydown.esc="cancel"
      @blur="save"
      @click.native.stop
    />

    <!-- ref: built-in picker + actions slot -->
    <template v-else-if="type === 'ref'">
      <cbs-inline-picker
        ref="picker"
        :options="options"
        :display-val="displayVal || String(value || '')"
        :label-key="labelKey"
        :placeholder="placeholder"
        @select="$emit('select', $event)"
      />
      <slot name="actions" />
    </template>

    <!-- filter: toggle + summary + expanded constructor -->
    <template v-else-if="type === 'filter'">
      <span
        class="cbs-inline-attr__filter-toggle"
        @click.stop="filterExpanded = !filterExpanded"
      >
        <feather-icon
          :icon="filterExpanded ? 'ChevronDownIcon' : 'ChevronRightIcon'"
          size="12"
        />
      </span>
      <span
        class="cbs-inline-attr__filter-summary"
        @click.stop="filterExpanded = !filterExpanded"
      >
        {{ computedFilterSummary }}
      </span>
      <div
        v-if="filterExpanded"
        class="cbs-inline-attr__filter-body"
        @click.stop
      >
        <cbs-filter-constructor
          :node="filterObj.node"
          :fields="fields"
          @save="$emit('save', filterObj)"
        />
      </div>
    </template>

    <!-- plain value display -->
    <span
      v-else
      class="cbs-inline-attr__val"
      :class="editable ? 'cbs-inline-attr__val--editable' : 'cbs-inline-attr__val--muted'"
      :title="editable ? 'Двойной клик для редактирования' : undefined"
      @dblclick.stop="startEdit"
    >{{ displayVal !== null ? (displayVal || '—') : (value || '—') }}</span>

    <slot />
  </div>
</template>

<script>
import { BFormInput } from 'bootstrap-vue'
import CbsInlinePicker from '@/cubus/components/inline-picker/CbsInlinePicker.vue'
import CbsFilterConstructor from '@/cubus/components/filter/CbsFilterConstructor.vue'

export default {
  name: 'CbsInlineAttribute',
  components: { BFormInput, CbsInlinePicker, CbsFilterConstructor },
  props: {
    label: {
      type: String,
      required: true,
    },
    value: {
      type: [String, Number],
      default: '',
    },
    displayVal: {
      type: String,
      default: null,
    },
    type: {
      type: String,
      default: 'text',
      validator: v => ['text', 'ref', 'filter'].includes(v),
    },
    // ref props
    options: {
      type: Array,
      default: () => [],
    },
    labelKey: {
      type: String,
      default: 'title',
    },
    placeholder: {
      type: String,
      default: 'Поиск...',
    },
    // filter props
    filterObj: {
      type: Object,
      default: null,
    },
    fields: {
      type: Array,
      default: () => [],
    },
    // common
    editable: {
      type: Boolean,
      default: true,
    },
    focused: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      editing: false,
      editVal: '',
      filterExpanded: false,
    }
  },
  computed: {
    computedFilterSummary() {
      if (!this.filterObj || !this.filterObj.node) return '(пусто)'
      return this.nodeSummary(this.filterObj.node)
    },
  },
  methods: {
    // text attribute methods
    startEdit() {
      if (!this.editable || this.type !== 'text') return
      this.editVal = this.value != null ? String(this.value) : ''
      this.editing = true
      this.$nextTick(() => {
        if (this.$refs.input) this.$refs.input.focus()
      })
    },
    save() {
      if (!this.editing) return
      this.editing = false
      this.$emit('save', this.editVal.trim())
    },
    cancel() {
      this.editing = false
    },
    // ref picker proxy methods
    toggle() {
      if (this.$refs.picker) this.$refs.picker.toggle()
    },
    close() {
      if (this.$refs.picker) this.$refs.picker.close()
    },
    // filter toggle method (for keyboard nav)
    toggleFilter() {
      this.filterExpanded = !this.filterExpanded
    },
    // filter summary helpers
    nodeSummary(node) {
      if (!node) return ''
      const operName = (node.oper && node.oper.sid === 'or') ? 'ИЛИ' : 'И'
      const parts = []
      if (node.conds && node.conds.length) {
        node.conds.forEach(c => { if (c.isactive) parts.push(this.condSummary(c)) })
      }
      if (node.nodes && node.nodes.length) {
        node.nodes.forEach(n => { if (n.isactive) parts.push(this.nodeSummary(n)) })
      }
      if (!parts.length) return '(пусто)'
      if (parts.length === 1) return parts[0]
      return `${operName}(${parts.join(', ')})`
    },
    condSummary(cond) {
      if (!cond || !cond.args || !cond.args.length) return '?'
      const operName = (cond.oper && cond.oper.name) || '?'
      const arg0 = this.argSummary(cond.args[0])
      if (cond.args.length < 2 || (cond.oper && cond.oper.args === 1)) return `${arg0} ${operName}`
      return `${arg0} ${operName} ${this.argSummary(cond.args[1])}`
    },
    argSummary(arg) {
      if (!arg) return '?'
      const t = arg.type && arg.type.sid
      if (t === 'entity' || t === 'paramentity' || t === 'sourceentity') {
        const fld = this.fields.find(f => (arg.entitysid && f.entitysid === arg.entitysid)
          || (arg.value != null && f.value === arg.value))
        if (fld) return fld.title || fld.entitysid || String(fld.value)
        if (arg.entitysid) return arg.entitysid
        return arg.value != null ? String(arg.value) : '?'
      }
      if (t === 'value') {
        if (arg.value === true) return 'ИСТИНА'
        if (arg.value === false) return 'ЛОЖЬ'
        if (arg.value == null || arg.value === '') return '?'
        return String(arg.value)
      }
      if (arg.value != null) return String(arg.value)
      return '?'
    },
  },
}
</script>

<style lang="scss" scoped>
.cbs-inline-attr {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  padding: 2px 4px;
  border-radius: 4px;
  cursor: default;

  &--focused {
    background: rgba(115, 103, 240, 0.08);
    outline: 1px solid rgba(115, 103, 240, 0.3);
  }

  &--block {
    flex-wrap: wrap;
    align-items: flex-start;
  }

  &__key {
    color: #b9b9c3;
    min-width: 56px;
    flex-shrink: 0;
  }

  &__val {
    color: #6e6b7b;

    &--muted {
      color: #b9b9c3;
      font-size: 12px;
    }

    &--editable {
      cursor: text;
      border-bottom: 1px dashed transparent;

      &:hover {
        border-bottom-color: #b9b9c3;
      }
    }
  }

  &__input {
    font-size: 12px;
    height: 24px;
    padding: 0 6px;
    max-width: 260px;
  }

  &__filter-toggle {
    color: #b9b9c3;
    flex-shrink: 0;
    cursor: pointer;

    &:hover {
      color: #7367f0;
    }
  }

  &__filter-summary {
    color: #6e6b7b;
    font-size: 12px;
    cursor: pointer;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    &:hover {
      color: #7367f0;
    }
  }

  &__filter-body {
    width: 100%;
    margin-top: 4px;
    border: 1px solid #ebe9f1;
    border-radius: 4px;
    padding: 4px 6px;
  }
}

.dark-layout {
  .cbs-inline-attr {
    &--focused {
      background: rgba(115, 103, 240, 0.15);
      outline-color: rgba(115, 103, 240, 0.4);
    }

    &__val {
      color: #b4b7bd;
    }

    &__filter-summary {
      color: #b4b7bd;
    }

    &__filter-body {
      border-color: #3b4253;
    }
  }
}
</style>
